import { useState } from 'react';
import axios from 'axios';
import { AVAILABLE_VOICES } from '../ItineraryCreator/utils/voiceConstants';
import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';

const ERINOME_CITYRAMA_STYLE_PREFIX = '[high-pitched], [playful], [excited]';

const applyErinomeCityramaPrefix = (text) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return ERINOME_CITYRAMA_STYLE_PREFIX;
  if (normalizedText.startsWith(ERINOME_CITYRAMA_STYLE_PREFIX)) {
    return normalizedText;
  }
  return `${ERINOME_CITYRAMA_STYLE_PREFIX} ${normalizedText}`;
};

export const useSimpleAudioGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [chunkProgress, setChunkProgress] = useState(null); // Track chunk progress: { current, total, status }

  const getBaseFilename = (filename) => {
    if (!filename) return '';
    return filename.replace(/\.[^/.]+$/, '');
  };

  const normalizeTtsErrorMessage = (error) => {
    const payload = error?.response?.data;
    const messageParts = [];

    if (payload?.error) {
      messageParts.push(payload.error);
    }

    if (payload?.message) {
      messageParts.push(payload.message);
    }

    if (typeof payload === 'string' && payload.trim()) {
      messageParts.push(payload.trim());
    }

    if (error?.message && !messageParts.includes(error.message)) {
      messageParts.push(error.message);
    }

    const combinedMessage = messageParts.join(' | ');
    const lowerMessage = combinedMessage.toLowerCase();

    if (
      lowerMessage.includes('quota exceeded') ||
      lowerMessage.includes('resource_exhausted') ||
      lowerMessage.includes('rate limit')
    ) {
      return `Selected TTS voice is currently quota-limited upstream. ${combinedMessage}`;
    }

    return combinedMessage || 'Failed to generate audio';
  };

  const shouldRetryTtsRequest = (error) => {
    const code = error?.code || '';
    const message = (error?.message || '').toLowerCase();
    return (
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      message.includes('err_connection_closed') ||
      message.includes('network error') ||
      message.includes('socket hang up') ||
      message.includes('failed to fetch')
    );
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const postTtsRequest = async (endpoint, payload, axiosConfig, retryContext = {}) => {
    const maxAttempts = 2;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        if (attempt > 1) {
          console.warn('[TTS Retry] Retrying transient TTS request', {
            attempt,
            ...retryContext
          });
        }

        return await axios.post(endpoint, payload, axiosConfig);
      } catch (error) {
        const canRetry = attempt < maxAttempts && shouldRetryTtsRequest(error);
        console.error('[TTS Request Failed]', {
          attempt,
          canRetry,
          code: error?.code,
          message: error?.message,
          ...retryContext
        });

        if (!canRetry) {
          throw error;
        }

        await delay(1500);
      }
    }
  };

  

  /**
   * NEW FUNCTION: Converts SSML to Chirp3-compatible markup for pause control.
   * @param {string} ssmlText The incoming SSML string.
   * @returns {string} Text with [pause] tags and no SSML.
   */
  const convertSsmlToChirpMarkup = (ssmlText) => {
    console.log("=== CHIRP MARKUP CONVERSION DEBUG ===");
    console.log("Input SSML:", ssmlText.substring(0, 200) + "...");

    let markupText = ssmlText
      // 1. Replace SSML break tags with Chirp markup tags. Adding spaces for clarity.
      .replace(/<break[^>]*\/>/gi, ' [pause] ')
      .replace(/<break[^>]*><\/break>/gi, ' [pause] ')
      // 2. Remove all other SSML tags
      .replace(/<\/?(speak|prosody)[^>]*>/gi, '')
      // 3. Clean up spacing
      .replace(/\s+/g, ' ')
      .trim();
      
    console.log("Converted Markup:", markupText.substring(0, 200) + "...");
    console.log("Markup text length:", markupText.length);
    console.log("=== END CHIRP MARKUP CONVERSION DEBUG ===");

    return markupText;
  };

  /**
   * Splits text into chunks for SimpleTTS to maintain audio quality.
   * Target chunk size: ~2000 characters (roughly 80-90 seconds of audio)
   * Splits at natural breakpoints: paragraphs > sentences > word boundaries
   *
   * CHUNK SIZE CONFIGURATION:
   * - Smaller chunks (1500): More splits, better quality, longer processing
   * - Larger chunks (2500): Fewer splits, faster, but quality may degrade after ~2min
   *
   * @param {string} text The text to split
   * @param {number} maxChunkSize Maximum characters per chunk (default 2000)
   * @returns {string[]} Array of text chunks
   */
  
  // HOW CHUNKING WORKS:
  // 1. Split the text into paragraphs using \n\n (that's how our JSON stores paragraph breaks)
  // 2. Group consecutive paragraphs together until adding another would exceed CHUNK_SIZE
  // 3. Each chunk ALWAYS ends at a paragraph boundary — never mid-paragraph, never mid-sentence
  // 4. If a single paragraph is longer than CHUNK_SIZE, it stays as one chunk (we don't cut it)
  const getDefaultChunkSizeForVoice = (voiceId) => {
    if (!voiceId) return 800;
    // Gemini 3.8 stays stable on long audio, so only very long texts get split
    if (voiceId.includes('SimpleTTS-38')) return 5000;
    if (voiceId.includes('Erinome')) return 600;
    if (voiceId.includes('SimpleTTS-Legacy') || voiceId.includes('SimpleTTS-Pro')) return 1000;
    if (voiceId.includes('SimpleTTS')) return 800;
    return 800;
  };
  const MIN_CHUNK_SIZE = 100;

  const resolveChunkSize = (value, fallback = 800) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(MIN_CHUNK_SIZE, Math.floor(parsed));
  };

  const splitTextIntoChunks = (text, maxChunkSize = 800) => {
    if (!text || text.length <= maxChunkSize) {
      return [text];
    }

    // DEBUG: Log what line-ending chars exist in the text so we can see why splitting fails
    const hasLF = text.includes('\n');
    const hasCR = text.includes('\r');
    const hasDoubleLF = text.includes('\n\n');
    const hasCRLF = text.includes('\r\n');
    const hasDoubleCRLF = text.includes('\r\n\r\n');
    console.log(`%c[SimpleTTS Chunking] Text analysis:`, 'color: #FF9800; font-weight: bold', {
      length: text.length,
      hasLF, hasCR, hasDoubleLF, hasCRLF, hasDoubleCRLF,
      first200: text.substring(0, 200).replace(/\n/g, '↵').replace(/\r/g, '⏎')
    });

    // Normalize line endings: \r\n → \n (Windows → Unix)
    const normalized = text.replace(/\r\n/g, '\n');

    // Split into individual paragraphs
    const paragraphs = normalized.split('\n\n').map(p => p.trim()).filter(p => p.length > 0);

    // If no paragraph breaks exist, fall back to sentence-boundary splitting.
    // This handles texts that were written as one big block with no \n\n.
    if (paragraphs.length <= 1) {
      const chunks = [];
      let remaining = text.trim();
      while (remaining.length > maxChunkSize) {
        const searchText = remaining.substring(0, maxChunkSize);
        let lastSentenceEnd = -1;
        for (const match of searchText.matchAll(/[.!?]\s+(?=[A-Z])/g)) {
          if (match.index > maxChunkSize * 0.4) lastSentenceEnd = match.index + match[0].length;
        }
        const splitPoint = lastSentenceEnd > 0 ? lastSentenceEnd : (searchText.lastIndexOf(' ') + 1 || maxChunkSize);
        chunks.push(remaining.substring(0, splitPoint).trim());
        remaining = remaining.substring(splitPoint).trim();
      }
      if (remaining) chunks.push(remaining);
      return chunks;
    }

    // Group paragraphs into chunks, respecting CHUNK_SIZE as a soft limit
    const chunks = [];
    let currentChunk = '';

    for (const paragraph of paragraphs) {
      const wouldBe = currentChunk ? currentChunk + '\n\n' + paragraph : paragraph;

      if (currentChunk && wouldBe.length > maxChunkSize) {
        // Adding this paragraph would exceed the limit — flush current chunk
        chunks.push(currentChunk);
        currentChunk = paragraph;
      } else {
        // Still fits (or first paragraph in chunk) — accumulate
        currentChunk = wouldBe;
      }
    }

    // Don't forget the last chunk
    if (currentChunk) {
      chunks.push(currentChunk);
    }

    // Browser console — visible in DevTools when generating audio
    console.group(`%c[SimpleTTS] Split into ${chunks.length} chunk(s) at paragraph boundaries`, 'color: #4CAF50; font-weight: bold');
    chunks.forEach((chunk, i) => {
      const paragraphCount = chunk.split('\n\n').length;
      console.log(`  Chunk ${i + 1}/${chunks.length} — ${chunk.length} chars, ${paragraphCount} paragraph(s) — "${chunk.substring(0, 80).replace(/\n/g, '↵')}..."`);
    });
    console.groupEnd();

    return chunks;
  };

  /**
   * Estimates audio duration based on text length.
   * Average speaking rate: ~150 words per minute, ~750 characters per minute
   * @param {string} text The text to estimate
   * @returns {number} Estimated duration in seconds
   */
  const estimateAudioDuration = (text) => {
    const charsPerMinute = 750;
    const chars = text.length;
    return (chars / charsPerMinute) * 60;
  };

  const generateAudio = async (text, poi, itineraryMetadata, voiceId, speechRate = '1.0', options = {}) => {
    try {
      setIsGenerating(true);
      setProgress(10);
      setError(null);
      setChunkProgress(null); // Reset chunk progress
      return await generateSingleAudio(text, poi, itineraryMetadata, voiceId, speechRate, options);
    } catch (error) {
      console.error('TTS Generation Error:', error);
      const normalizedMessage = normalizeTtsErrorMessage(error);
      setError(normalizedMessage);
      throw new Error(normalizedMessage);
    } finally {
      setIsGenerating(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const generateSingleAudio = async (text, poi, itineraryMetadata, voiceId, speechRate, options) => {
    const startTime = performance.now();
    const voiceConfig = AVAILABLE_VOICES[voiceId];
    const legacySimpleTtsReplacement = typeof voiceId === 'string' && voiceId.includes('SimpleTTS-Legacy')
      ? voiceId.replace('SimpleTTS-Legacy', 'SimpleTTS')
      : '';
    const upgradedVoiceConfig = legacySimpleTtsReplacement ? AVAILABLE_VOICES[legacySimpleTtsReplacement] : null;
    const effectiveVoiceId = upgradedVoiceConfig ? legacySimpleTtsReplacement : voiceId;
    const effectiveVoiceConfig = upgradedVoiceConfig || voiceConfig;
    if (!effectiveVoiceConfig) throw new Error(`Unknown voice ID: ${voiceId}`);

    // Check if the selected voice is a Chirp voice, which requires special handling.
    const isChirpVoice = effectiveVoiceId.includes('Chirp3-HD');

    // Check if this is a Gemini voice (has useGeminiTts or useGeminiApi flag)
    const isGeminiVoice = effectiveVoiceConfig.useGeminiTts === true;
    const useGeminiApi = effectiveVoiceConfig.useGeminiApi === true;

    // Check if this voice should use SimpleTTS method for highest quality
    const useSimpleTtsMethod = effectiveVoiceConfig.useSimpleTtsMethod === true;
    const chunkingEnabled = options.chunkingEnabled !== false;
    const resolvedChunkSize = resolveChunkSize(options.chunkSize, getDefaultChunkSizeForVoice(effectiveVoiceId));

    // Prepare text based on voice type:
    // - Chirp voices: Convert SSML to Chirp markup
    // - Gemini voices: Strip all SSML tags (Gemini doesn't support SSML)
    // - Other voices: Use SSML as-is
    let textToSend = text;
    if (isChirpVoice) {
      textToSend = convertSsmlToChirpMarkup(text);
    } else if (isGeminiVoice) {
      // Strip SSML tags for Gemini - it only accepts plain text.
      // IMPORTANT: <break time="0.85s"/> tags were originally \n\n paragraph breaks
      // (convertToSSML converts \n\n → <break>). We restore them to \n\n here so that
      // the chunking logic can split at paragraph boundaries.
      textToSend = text
        .replace(/<speak>/gi, '')
        .replace(/<\/speak>/gi, '')
        .replace(/<break\s+time="0\.85s"\s*\/>/gi, '\n\n')  // paragraph breaks → restore \n\n
        .replace(/<break[^>]*\/>/gi, ' ')                     // other breaks → space
        .replace(/<break[^>]*><\/break>/gi, ' ')
        .replace(/<\/?(prosody|voice|emphasis|say-as|phoneme|sub|lang|p|s)[^>]*>/gi, '')
        .replace(/\r\n/g, '\n')              // normalize Windows line endings
        .replace(/ +/g, ' ')                 // collapse multiple spaces
        .trim();

      // Calculate byte size (browser-compatible)
      const getByteSize = (str) => new Blob([str]).size;
      const textBytes = getByteSize(textToSend);

      console.log('🔄 Stripped SSML for Gemini voice:');
      console.log('   - Original text length:', text.length);
      console.log('   - Cleaned text length:', textToSend.length);
      console.log('   - Text preview:', textToSend.substring(0, 150) + '...');
      console.log('   - Text bytes:', textBytes);
    }

    if (effectiveVoiceConfig.voiceId === 'Erinome') {
      textToSend = applyErinomeCityramaPrefix(textToSend);

      console.log('[Erinome Debug] Final text being sent to Gemini TTS:', textToSend);
    }

    // Sanitize country name to ensure no spaces in storage paths (use underscores)
    const sanitizedCountry = (itineraryMetadata.country || 'Unknown')
      .trim()
      .toLowerCase()
      .replace(/[^\w]/g, '_')
      .replace(/_+/g, '_');

    const storageConfig = {
      country: sanitizedCountry,
      itineraryName: getBaseFilename(itineraryMetadata.filename) || itineraryMetadata.name || 'Unnamed Itinerary',
      poiName: poi.name || 'Unnamed POI',
      language: itineraryMetadata.language || 'en',
    };

    // =====================================================
    // SIMPLETSTS CHUNKING LOGIC FOR LONG TEXT
    // =====================================================
    // SimpleTTS (Aoede) voices degrade in quality after ~2 minutes of audio.
    // To maintain quality, we split long text into chunks and process separately.
    const needsChunking = chunkingEnabled && textToSend.length > resolvedChunkSize;

    console.log(`%c[SimpleTTS] voice=${voiceId} | textLength=${textToSend.length} | chunkingEnabled=${chunkingEnabled} | CHUNK_SIZE=${resolvedChunkSize} | needsChunking=${needsChunking}`, 'color: #2196F3');

    if (needsChunking) {
      console.log('🔀 === SIMPLETSTS CHUNKING ENABLED ===');
      console.log(`   Text length: ${textToSend.length} chars (threshold: ${resolvedChunkSize})`);
      console.log(`   Estimated total duration: ~${Math.round(estimateAudioDuration(textToSend))} seconds`);

      const chunks = splitTextIntoChunks(textToSend, resolvedChunkSize).map((chunk) => (
        effectiveVoiceConfig.voiceId === 'Erinome'
          ? applyErinomeCityramaPrefix(chunk)
          : chunk
      ));
      console.log(`   Split into ${chunks.length} chunks for processing`);
      if (effectiveVoiceConfig.voiceId === 'Erinome') {
        console.log('[Erinome Debug] First chunk being sent to Gemini TTS:', chunks[0]);
        if (chunks.length > 1) {
          console.log('[Erinome Debug] Second chunk being sent to Gemini TTS:', chunks[1]);
        }
      }

      // Update chunk progress state
      setChunkProgress({ current: 0, total: chunks.length, status: 'starting' });

      // Call onChunkProgress callback if provided
      if (options.onChunkProgress) {
        options.onChunkProgress({ current: 0, total: chunks.length, status: 'starting', poiName: poi.name });
      }

      // Prepare base request data for chunked processing
      const baseRequestData = {
      provider: effectiveVoiceConfig.provider.toLowerCase(),
      voiceId: effectiveVoiceConfig.voiceId,
      languageCode: effectiveVoiceConfig.languageCode,
        storageConfig: storageConfig,
        isPlainText: true,
        isMarkup: isChirpVoice,
        azureLanguage: voiceConfig.azureLanguage,
        azureVoiceName: voiceConfig.azureVoiceName,
        useGeminiApi: useGeminiApi,
        useGeminiTts: isGeminiVoice && !useGeminiApi && !useSimpleTtsMethod,
        useSimpleTtsMethod,
        ...(voiceConfig.model && { model: voiceConfig.model }),
        // Chunking metadata
        isChunkedRequest: true,
        totalChunks: chunks.length,
        chunkingEnabled,
        chunkSize: resolvedChunkSize,
        chunks: chunks, // Send all chunks at once for backend to process
      };

      // Add geminiPrompt if provided
      if (options.geminiPrompt && options.geminiPrompt.trim().length > 0) {
        const trimmedPrompt = options.geminiPrompt.trim();
        const getByteSize = (str) => new Blob([str]).size;
        const promptBytes = getByteSize(trimmedPrompt);
        if (promptBytes > 900) {
          baseRequestData.geminiPrompt = trimmedPrompt.substring(0, 200);
        } else {
          baseRequestData.geminiPrompt = trimmedPrompt;
        }
      }

      // Add temperature if provided
      if (options.temperature !== undefined) {
        baseRequestData.temperature = options.temperature;
      }

      console.log('📤 Sending chunked TTS request to backend...');
      console.log(`   Chunks: ${chunks.length}, Total chars: ${textToSend.length}`);

      const API_ENDPOINT = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/proxyItineraryBuilderNewTtsRequest';

      try {
        // Update progress for chunked request
        setProgress(20);
        setChunkProgress({ current: 0, total: chunks.length, status: 'sending' });

        if (options.onChunkProgress) {
          options.onChunkProgress({ current: 0, total: chunks.length, status: 'processing', poiName: poi.name });
        }

        const token = await getFirebaseAuthToken();
        const axiosResponse = await postTtsRequest(API_ENDPOINT, baseRequestData, {
          timeout: 600000, // 10 minutes for chunked requests
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }, {
          poiName: poi.name,
          chunked: true,
          chunkCount: chunks.length,
          voiceId
        });

        if (!axiosResponse.data || !axiosResponse.data.audioUrl) {
          throw new Error('Invalid response from TTS service: audioUrl missing.');
        }

        const endTime = performance.now();
        const generationTimeSeconds = ((endTime - startTime) / 1000).toFixed(2);

        // Update final progress
        setProgress(100);
        setChunkProgress({ current: chunks.length, total: chunks.length, status: 'completed' });

        if (options.onChunkProgress) {
          options.onChunkProgress({ current: chunks.length, total: chunks.length, status: 'completed', poiName: poi.name });
        }

        console.log(`✅ Chunked audio generation completed in ${generationTimeSeconds}s`);
        console.log(`   Chunks processed: ${chunks.length}`);
        console.log(`   Final audio URL: ${axiosResponse.data.audioUrl}`);

        const rawAudioUrl = axiosResponse.data.compressedAudioUrl
          || axiosResponse.data.audioUrl;
        const originalAudioUrl = axiosResponse.data.uncompressedAudioUrl
          || axiosResponse.data.originalAudioUrl
          || rawAudioUrl;
        const audioUrl = rawAudioUrl;

        return {
          audioUrl,
          compressedAudioUrl: audioUrl,
          originalAudioUrl,
          uncompressedAudioUrl: originalAudioUrl,
          audioDuration: axiosResponse.data.audioDuration || 0,
          generationTime: generationTimeSeconds,
          metadata: {
            ...axiosResponse.data.metadata,
            voiceId,
            speechRate,
            isMarkup: isChirpVoice,
            generationTime: generationTimeSeconds,
            chunked: true,
            chunkCount: chunks.length
          },
        };
      } catch (error) {
        setChunkProgress({ current: 0, total: chunks.length, status: 'error' });
        if (options.onChunkProgress) {
          options.onChunkProgress({ current: 0, total: chunks.length, status: 'error', poiName: poi.name, error: error.message });
        }
        throw error;
      }
    }

    // =====================================================
    // STANDARD (NON-CHUNKED) PROCESSING
    // =====================================================
    // Prepare base request data
    const requestData = {
      provider: effectiveVoiceConfig.provider.toLowerCase(), // Backend expects lowercase provider
      text: textToSend,
      voiceId: effectiveVoiceConfig.voiceId,
      languageCode: effectiveVoiceConfig.languageCode,
      storageConfig: storageConfig,
      isPlainText: useSimpleTtsMethod || isGeminiVoice,
      // Add a new flag to tell the backend to use the 'markup' field for Chirp voices.
      // This will be false for standard voices, and the backend will default to SSML.
      isMarkup: isChirpVoice,
      // Azure-specific parameters for multilingual voices
      azureLanguage: effectiveVoiceConfig.azureLanguage,
      azureVoiceName: effectiveVoiceConfig.azureVoiceName,
      // Gemini-specific parameters
      useGeminiApi: useGeminiApi,
      useGeminiTts: isGeminiVoice && !useGeminiApi && !useSimpleTtsMethod, // Only set useGeminiTts if NOT using Gemini API or SimpleTTS
      useSimpleTtsMethod: useSimpleTtsMethod, // Preserve the selected voice behavior
      chunkingEnabled,
      chunkSize: resolvedChunkSize,
      ...(effectiveVoiceConfig.model && { model: effectiveVoiceConfig.model }),
    };

    // Add geminiPrompt only if it's provided and not empty
    if (options.geminiPrompt && options.geminiPrompt.trim().length > 0) {
      const trimmedPrompt = options.geminiPrompt.trim();
      const getByteSize = (str) => new Blob([str]).size;
      const promptBytes = getByteSize(trimmedPrompt);

      console.log('📝 Gemini style prompt:');
      console.log('   - Prompt:', trimmedPrompt);
      console.log('   - Prompt bytes:', promptBytes);

      // Gemini has a 900 byte limit for prompts
      if (promptBytes > 900) {
        console.warn('⚠️ Warning: Prompt exceeds 900 bytes, truncating...');
        requestData.geminiPrompt = trimmedPrompt.substring(0, 200); // Safe truncation
      } else {
        requestData.geminiPrompt = trimmedPrompt;
      }
    }

    // Add temperature if provided
    if (options.temperature !== undefined && isGeminiVoice) {
      requestData.temperature = options.temperature;
      console.log(`🌡️ Temperature for Gemini voice: ${options.temperature}`);
    }

    // Add speech rate parameter - but NOT for Gemini voices (they don't support it yet)
    // Gemini TTS API doesn't have a speed/rate parameter in their current API
    if (!isGeminiVoice) {
      requestData.speechRate = parseFloat(speechRate) || 1.0;
      console.log(`Speech rate ${requestData.speechRate}x will be applied (provider: ${effectiveVoiceConfig.provider})`);
    } else {
      console.log('⚠️ Gemini voices do not support dynamic speech rate adjustment. Use voice style controls instead.');
    }

    console.log(`Sending TTS request (Markup Mode: ${isChirpVoice}, Gemini: ${isGeminiVoice}) for a text of ${textToSend.length} characters.`);
    console.log('Request data preview:', {
      provider: requestData.provider,
      voiceId: requestData.voiceId,
      languageCode: requestData.languageCode,
      speechRate: requestData.speechRate,
      azureVoiceName: requestData.azureVoiceName,
      azureLanguage: requestData.azureLanguage,
      useGeminiTts: requestData.useGeminiTts,
      model: requestData.model,
      textLength: requestData.text.length,
      textPreview: requestData.text.substring(0, 100) + '...',
      isMarkup: requestData.isMarkup
    });

    const API_ENDPOINT = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/proxyItineraryBuilderNewTtsRequest';

    try {
      setProgress(50);
      console.log('Full request data being sent:', JSON.stringify(requestData, null, 2));
      const token = await getFirebaseAuthToken();
      const axiosResponse = await postTtsRequest(API_ENDPOINT, requestData, {
        timeout: 360000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }, {
        poiName: poi.name,
        chunked: false,
        voiceId
      });

      if (!axiosResponse.data || !axiosResponse.data.audioUrl) {
        console.error('Invalid API response:', axiosResponse.data);
        throw new Error('Invalid response from TTS service: audioUrl missing.');
      }
      
      const endTime = performance.now();
      const generationTimeSeconds = ((endTime - startTime) / 1000).toFixed(2);

      const rawAudioUrl = axiosResponse.data.compressedAudioUrl
        || axiosResponse.data.audioUrl;
      const originalAudioUrl = axiosResponse.data.uncompressedAudioUrl
        || axiosResponse.data.originalAudioUrl
        || rawAudioUrl;
      const audioUrl = rawAudioUrl;

      setProgress(100);
      console.log(`✅ Audio generation completed in ${generationTimeSeconds}s`);

      return {
        audioUrl,
        compressedAudioUrl: audioUrl,
        originalAudioUrl,
        uncompressedAudioUrl: originalAudioUrl,
        audioDuration: axiosResponse.data.audioDuration || 0,
        generationTime: generationTimeSeconds,
        metadata: { ...axiosResponse.data.metadata, voiceId, speechRate, isMarkup: isChirpVoice, generationTime: generationTimeSeconds },
      };
    } catch (error) {
      console.error('Full error object:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      const payload = error.response?.data;
      const errorMessage = [
        payload?.error,
        payload?.message,
        typeof payload === 'string' ? payload : '',
        error.message
      ].filter(Boolean).join(' | ') || 'An unknown error occurred.';
      console.error('API call failed:', errorMessage);
      throw new Error(errorMessage);
    }
  };
  
  const generateBatchAudio = async (poisWithText, commonConfig, onProgress) => {
    const results = [];
    const errors = [];
    
    for (let i = 0; i < poisWithText.length; i++) {
      const { poi, text } = poisWithText[i];
      
      try {
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: poisWithText.length,
            currentPoi: poi.name
          });
        }
        
        const result = await generateSingleAudio(
          text, 
          poi, 
          commonConfig.itineraryMetadata, 
          commonConfig.voiceId, 
          commonConfig.speechRate,
          commonConfig.options
        );
        
        results.push({ poi, result });
      } catch (error) {
        console.error(`Failed to generate audio for POI ${poi.name}:`, error);
        errors.push({ poi, error: error.message });
      }
    }
    
    return { results, errors };
  };

  return { generateAudio, generateBatchAudio, isGenerating, error, progress, chunkProgress };
};
